import { Alert, Anchor, Badge, Button, Group, Stack, Table } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { DataTableShell, PageHeader, SortableTh, StatusBadge, useInfinitePage, useTableSort } from "../../components/ui";
import { jobsApi } from "../../lib/api";
import { NAV_COPY, NAV_ICONS } from "../../lib/navCopy";
import { formatDate, SEGMENT_LABELS } from "../../lib/format";

export function HrJobsPage() {
  const jobsQuery = useInfinitePage(["hr-jobs", "list"], (page, pageSize) =>
    jobsApi.list({ page, page_size: pageSize }),
  );

  const jobs = jobsQuery.items;
  const pendingReviewCount = jobs.filter((j) => j.status === "pending_review").length;

  const { sorted, sortKey, direction, toggle } = useTableSort(jobs, {
    title: (r) => r.title?.toLowerCase() ?? "",
    segment: (r) => (SEGMENT_LABELS[r.segment] ?? r.segment).toLowerCase(),
    status: (r) => r.status,
    openings: (r) => r.openings_filled,
    created: (r) => r.created_at,
  });

  return (
    <Stack gap="lg">
      <PageHeader
        icon={NAV_ICONS.jobs}
        title={NAV_COPY.hr.jobs}
        subtitle="Create and manage job postings for your company."
        actions={
          <Button component={Link} to="/hr/jobs/new" leftSection={<IconPlus size="var(--axio-unit-16)" />} color="violet">
            {NAV_COPY.hr.postJob}
          </Button>
        }
      />

      {pendingReviewCount > 0 && (
        <Alert color="orange" variant="light" title="Pending AxioHire review">
          {pendingReviewCount} job{pendingReviewCount > 1 ? "s are" : " is"} awaiting fee approval.
          Recruiters will see {pendingReviewCount > 1 ? "them" : "it"} once AxioHire confirms the fee
          split.
        </Alert>
      )}

      <DataTableShell
        loading={jobsQuery.isLoading}
        error={
          jobsQuery.isError
            ? jobsQuery.error instanceof Error
              ? jobsQuery.error.message
              : undefined
            : undefined
        }
        onRetry={() => void jobsQuery.refetch()}
        hasMore={jobsQuery.hasMore}
        loadingMore={jobsQuery.loadingMore}
        onLoadMore={jobsQuery.loadMore}
        isEmpty={jobs.length === 0}
        empty={{
          icon: NAV_ICONS.jobs,
          title: "No jobs yet",
          description: "Post your first job to start receiving submissions.",
          action: (
            <Button component={Link} to="/hr/jobs/new" variant="default">
              Post your first job
            </Button>
          ),
        }}
      >
        <Table verticalSpacing="md" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <SortableTh columnKey="title" sortKey={sortKey} direction={direction} onSort={toggle}>
                Title
              </SortableTh>
              <SortableTh columnKey="segment" sortKey={sortKey} direction={direction} onSort={toggle}>
                Segment
              </SortableTh>
              <SortableTh columnKey="status" sortKey={sortKey} direction={direction} onSort={toggle}>
                Status
              </SortableTh>
              <SortableTh columnKey="openings" sortKey={sortKey} direction={direction} onSort={toggle}>
                Openings
              </SortableTh>
              <SortableTh columnKey="created" sortKey={sortKey} direction={direction} onSort={toggle}>
                Created
              </SortableTh>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sorted.map((job) => (
              <Table.Tr key={job.id}>
                <Table.Td fw="var(--axio-font-weight-medium)">{job.title}</Table.Td>
                <Table.Td>
                  <Badge color="gray" variant="outline" size="sm">
                    {SEGMENT_LABELS[job.segment] ?? job.segment}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <StatusBadge status={job.status} />
                </Table.Td>
                <Table.Td className="tabular">
                  {job.openings_filled}/{job.openings_total}
                </Table.Td>
                <Table.Td>{formatDate(job.created_at)}</Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <Anchor component={Link} to={`/hr/jobs/${job.id}`} size="sm">
                      View
                    </Anchor>
                    {(job.status === "draft" || job.status === "paused") && (
                      <Anchor component={Link} to={`/hr/jobs/${job.id}/edit`} size="sm">
                        Edit
                      </Anchor>
                    )}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </DataTableShell>
    </Stack>
  );
}
